import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { CreditsProvider } from '@/context/CreditsContext';
import { Toaster } from '@/components/ui/sonner';
import ThemeProvider from '@/components/ThemeProvider';

// Pages
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import SetupPassword from '@/pages/SetupPassword';
import Dashboard from '@/pages/Dashboard';
import Clients from '@/pages/Clients';
import Settings from '@/pages/Settings';
import FunnelWizard from '@/pages/FunnelWizard';
import OAuthCallback from '@/pages/OAuthCallback';
import AdminPromptsDynamic from '@/pages/AdminPromptsDynamic';
import AdminPromptStats from '@/pages/AdminPromptStats';
import AdminAIConfig from '@/pages/AdminAIConfig';
import AdminAPIConfig from '@/pages/AdminAPIConfig';
import AdminMaster from '@/pages/AdminMaster';
import AdminEmailTemplates from '@/pages/AdminEmailTemplates';
import AdminUsuarios from '@/pages/AdminUsuarios';
import AdminSuscripciones from '@/pages/AdminSuscripciones';
import AdminTransacciones from '@/pages/AdminTransacciones';
import AdminSoporte from '@/pages/AdminSoporte';
import AdminSistema from '@/pages/AdminSistema';
import AdminTutoriales from '@/pages/AdminTutoriales';
import UserSupport from '@/pages/UserSupport';
import Tutorials from '@/pages/Tutorials';
import LandingPreview from '@/pages/LandingPreview';
import FunnelPreview from '@/pages/FunnelPreview';
import Billing from '@/pages/Billing';
import StrategyAdvisor from '@/pages/StrategyAdvisor';
import QuizLanding from '@/pages/QuizLanding';
import AuditorMarketing from '@/pages/AuditorMarketing';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import CookiesPolicy from '@/pages/CookiesPolicy';
import TermsOfService from '@/pages/TermsOfService';
import CheckoutSuccess from '@/pages/CheckoutSuccess';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Route Component (requires super_admin role)
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Fully Public Routes (no auth check) */}
      <Route path="/quiz" element={<QuizLanding />} />
      <Route path="/auditor" element={<AuditorMarketing />} />
      <Route path="/registro/quiz" element={<QuizLanding />} />
      <Route path="/registro/auditor" element={<AuditorMarketing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookies" element={<CookiesPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      
      {/* OAuth Callback Route - handles OAuth redirects */}
      <Route path="/oauth-callback" element={<OAuthCallback />} />
      
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/registro"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={<ForgotPassword />}
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      <Route
        path="/setup-password"
        element={<SetupPassword />}
      />
      
      {/* Protected Routes */}
      <Route
        path="/strategy-advisor"
        element={
          <ProtectedRoute>
            <StrategyAdvisor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/funnel/new"
        element={
          <ProtectedRoute>
            <FunnelWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proyectos"
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        }
      />
      {/* Legacy route redirect */}
      <Route path="/clientes" element={<Navigate to="/proyectos" replace />} />
      <Route
        path="/configuracion"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/soporte"
        element={
          <ProtectedRoute>
            <UserSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tutoriales"
        element={
          <ProtectedRoute>
            <Tutorials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/funnel/:id"
        element={
          <ProtectedRoute>
            <FunnelWizard />
          </ProtectedRoute>
        }
      />
      
      {/* Preview Route - Standalone full-screen editor */}
      <Route
        path="/preview/:landingId"
        element={<LandingPreview />}
      />
      
      {/* Funnel Preview Route - Public client approval page */}
      <Route
        path="/approval/:token"
        element={<FunnelPreview />}
      />
      <Route
        path="/f/:token"
        element={<FunnelPreview />}
      />

      {/* Admin Routes */}
      <Route
        path="/admin/prompts"
        element={
          <AdminRoute>
            <AdminPromptsDynamic />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/prompt-stats"
        element={
          <AdminRoute>
            <AdminPromptStats />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/ai-config"
        element={
          <AdminRoute>
            <AdminAIConfig />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/api-config"
        element={
          <AdminRoute>
            <AdminAPIConfig />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/master"
        element={
          <AdminRoute>
            <AdminMaster />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <AdminRoute>
            <AdminUsuarios />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/suscripciones"
        element={
          <AdminRoute>
            <AdminSuscripciones />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/transacciones"
        element={
          <AdminRoute>
            <AdminTransacciones />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/soporte"
        element={
          <AdminRoute>
            <AdminSoporte />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tutoriales"
        element={
          <AdminRoute>
            <AdminTutoriales />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/sistema"
        element={
          <AdminRoute>
            <AdminSistema />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/emails"
        element={
          <AdminRoute>
            <AdminEmailTemplates />
          </AdminRoute>
        }
      />

      {/* Default Redirect */}
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CreditsProvider>
          <ThemeProvider>
            <AppRoutes />
            <Toaster />
          </ThemeProvider>
        </CreditsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
